import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import $ from "jquery";
import { observable, runInAction } from "mobx";
import { ECheckoutCustomerType, EEcommerceSystemEnvironment } from "@devowl-wp/api-real-commerce";
import { Transaction } from "./transaction.js";
import { LOCAL_STORAGE_AFFILIATE_NAME } from "../../../others/createAffiliateStorageFromParams.js";
import { localStorageTest } from "../../../utils/localStorageTest.js";
import { CheckoutStep } from "../store/CheckoutV2Store.js";
const WAIT_PULL_COMPLETE_ORDER_INTERVAL = 5000;
class PaddleTransaction extends Transaction {
    /**
     * @see https://developer.paddle.com/guides/how-tos/checkout/inline-checkout
     */ get Paddle() {
        return this.paddle;
    }
    async loadVendorScripts() {
        // Delete affiliate cookies when coming through a recovery email
        if (this.isRecovered) {
            if (localStorageTest()) {
                localStorage.removeItem(LOCAL_STORAGE_AFFILIATE_NAME);
            }
        }
        // Load Paddle API once
        await $.getScript("https://cdn.paddle.com/paddle/paddle.js");
        this.paddle = window.Paddle;
        if (this.product.data.productPlan.product.ecommerceSystem.environment === EEcommerceSystemEnvironment.Sandbox) {
            this.paddle.Environment.set("sandbox");
        }
        this.paddle.Setup({
            vendor: +this.product.data.productPlan.product.ecommerceSystem.vendorId,
            eventCallback: this.eventCallback.bind(this)
        });
    }
    eventCallback(data) {
        this.waitCompletedOrder(data);
        this.updatePrices(data);
        const { event } = data;
        runInAction(()=>{
            if (event === "Checkout.Vat.Applied") {
                this.hasAddedVat = true;
                this.captureAppliedVat(data);
            } else if (event === "Checkout.Vat.Remove") {
                this.hasAddedVat = false;
                this.removePersistedVat();
            }
        });
    }
    /**
     * Pull the company-identity triple (VAT number + country) off Paddle's
     * `Checkout.Vat.Applied` payload and forward it to the backend so the
     * buyer's switch from individual → business mid-checkout survives
     * Edit Details (local identification) and a page reload (persisted
     * incomplete checkout).
     *
     * Paddle's inline-checkout event shape only exposes `vat_number` at the
     * root of `eventData` and `user.country`; the full billing address is
     * never part of the event payload and stays user-entered on our
     * identification form.
     */ captureAppliedVat(data) {
        try {
            const eventData = data?.eventData;
            if (!eventData || typeof eventData !== "object") {
                return;
            }
            const vatId = typeof eventData.vat_number === "string" ? eventData.vat_number.trim() : "";
            const countryRaw = typeof eventData.user?.country === "string" ? eventData.user.country.trim() : "";
            if (!vatId || !countryRaw) {
                return;
            }
            this.syncCompanyAddressWithIncompleteCheckout({
                customerType: ECheckoutCustomerType.Company,
                country: countryRaw.toUpperCase(),
                vatId
            });
        } catch  {
        // Paddle payload shape is unverified on every release — keep the
        // event pipeline resilient; the next identification-form submit
        // will replay the sync with the full address block anyway.
        }
    }
    /**
     * Create an interval to check for complete order (this does not mean that the payment is
     * complete, for this checkout `Transaction#applyPayment`!).
     *
     * **Why not using `successCallback` from Paddle SDK?** We had serious issues for some customers
     * as the callback got not called correctly. Paddle Support promised us that there is no known
     * similar case known by them. For more information see ticket `CU-hh8y3c`.
     */ waitCompletedOrder({ event, eventData }) {
        // Short circuit: Is it already awaiting payment?
        if (this.store.step >= CheckoutStep.PaymentProcessing) {
            this.stopCompleteOrderPoll();
            return;
        }
        // If we reach the needed event / step AND we are not already pulling the status
        if (event === "Checkout.PaymentMethodSelected" && !this.completeOrderPollTimer) {
            this.completeOrderPollTimer = setInterval(async ()=>{
                if (document.visibilityState === "hidden") {
                    return;
                }
                this.isPollRunning = true;
                try {
                    const { checkout: { ecommerceSystemCheckoutId } } = await this.fetchIsIncompleteCheckoutComplete();
                    if (ecommerceSystemCheckoutId) {
                        this.stopCompleteOrderPoll();
                        this.applyPayment(ecommerceSystemCheckoutId);
                    }
                } catch (e) {
                    this.error = e;
                } finally{
                    this.isPollRunning = false;
                }
            }, WAIT_PULL_COMPLETE_ORDER_INTERVAL);
        }
        // User did cancel the payment, lets disable pulling
        if (event === "Checkout.PaymentMethodChange") {
            this.stopCompleteOrderPoll();
        }
        // Payment got completed, lets disable pulling and directly apply payment
        if (event === "Checkout.PaymentComplete" && !this.isPollRunning) {
            this.stopCompleteOrderPoll();
            this.applyPayment(eventData.checkout.id);
        }
    }
    /**
     * Listen to all pricing changes including coupon-changes.
     *
     * @see https://developer.paddle.com/guides/how-tos/checkout/inline-checkout#updating-in-page-information
     */ updatePrices(data) {
        try {
            const { coupon, prices: { customer }, recurring_prices } = data.eventData.checkout;
            // Paddle's `unit` is the per-buyer **pre-coupon** value (gross
            // when VAT applies, net under reverse charge); `total` is the
            // per-buyer post-coupon value. Both reflect any country-level
            // VAT removal, so they're the right anchors for the Order
            // Summary's strike-through and discount-row visuals.
            this.applyNewPrices({
                initialPrice: +customer.total,
                initialPriceTax: +customer.total_tax,
                preCouponInitialPrice: +customer.unit,
                recurringPrice: recurring_prices ? +recurring_prices.customer.total : undefined,
                recurringPriceTax: recurring_prices ? +recurring_prices.customer.total_tax : undefined,
                preCouponRecurringPrice: recurring_prices ? +recurring_prices.customer.unit : undefined,
                coupon: coupon.coupon_code
            });
        } catch (e) {
            // Currently, we do ignore backend errors
            console.log(e);
        }
    }
    stopCompleteOrderPoll() {
        clearInterval(this.completeOrderPollTimer);
        this.completeOrderPollTimer = undefined;
    }
    constructor(...args){
        super(...args), this.hasAddedVat = false, /**
     * Avoids a race between the status poll and the Paddle "complete" event —
     * one flips on while the other is in flight.
     */ this.isPollRunning = false;
    }
}
_ts_decorate([
    observable
], PaddleTransaction.prototype, "hasAddedVat", void 0);
export { PaddleTransaction };
